import {
  ArrowUpTrayIcon,
  CloudArrowDownIcon,
  PhotoIcon,
  StarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid, TrashIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../../services/api';

interface VehicleImage {
  id: string;
  file_name: string;
  file_path: string;
  is_primary: boolean;
  display_order: number;
  isExternalUrl?: boolean;
}

interface VehicleImageUploadProps {
  vehicleId: string;
  images: VehicleImage[];
  onImagesChange: (images: VehicleImage[]) => void;
  maxImages?: number;
  onCloseModal?: () => void;
}

const VehicleImageUpload: React.FC<VehicleImageUploadProps> = ({
  vehicleId,
  images,
  onImagesChange,
  maxImages = 10,
  onCloseModal,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [loadingActions, setLoadingActions] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // When all displayed images are imported from an external source (e.g. CarsForSale),
  // the modal is read-only — uploads, selection, and reordering are managed at the source.
  const hasImages = images.length > 0;
  const allExternal = hasImages && images.every((img) => img.isExternalUrl === true);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      const uploadPromises = files.map(async (file, index) => {
        const fileId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        try {
          setUploading((prev) => [...prev, fileId]);

          // Validate file type
          const allowedTypes = [
            'image/png',
            'image/jpeg',
            'image/jpg',
            'image/gif',
            'image/webp',
            'image/heic',
            'image/heif',
          ];
          if (!allowedTypes.includes(file.type.toLowerCase())) {
            throw new Error(
              `File type ${file.type} not supported. Please use PNG, JPG, JPEG, GIF, WebP, HEIC, or HEIF.`
            );
          }

          // Validate file size (10MB limit)
          const maxSize = 10 * 1024 * 1024; // 10MB
          if (file.size > maxSize) {
            throw new Error(`File ${file.name} is too large. Maximum size is 10MB.`);
          }

          const formData = new FormData();
          formData.append('file', file);
          // Let backend decide primary status for batch uploads
          // Only send is_primary=true for the first image if no existing images
          const shouldBePrimary = images.length === 0 && index === 0;
          formData.append('is_primary', shouldBePrimary.toString());
          formData.append('display_order', (images.length + index).toString());

          const uploadedImage = await apiService.post<VehicleImage>(
            `/vehicles/${vehicleId}/vehicle-images`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            }
          );

          return uploadedImage;
        } catch (error: any) {
          console.error('Error uploading image:', error);
          const errorMessage =
            error.response?.data?.detail || error.message || `Failed to upload ${file.name}`;
          toast.error(errorMessage);
          return null;
        } finally {
          setUploading((prev) => prev.filter((id) => id !== fileId));
        }
      });

      const uploadedImages = (await Promise.all(uploadPromises)).filter(Boolean) as VehicleImage[];

      if (uploadedImages.length > 0) {
        onImagesChange([...images, ...uploadedImages]);
      }
    },
    [images, vehicleId, onImagesChange]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith('image/')
      );

      if (files.length + images.length > maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }

      await uploadFiles(files);
    },
    [images.length, maxImages, uploadFiles]
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []).filter((file) =>
        file.type.startsWith('image/')
      );

      if (files.length + images.length > maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }

      await uploadFiles(files);

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [images.length, maxImages, uploadFiles]
  );

  const handleSetPrimary = async (imageId: string) => {
    // Add loading state
    setLoadingActions((prev) => new Set(prev).add(`primary-${imageId}`));

    try {
      await apiService.patch(`/vehicles/${vehicleId}/vehicle-images/${imageId}`, {
        is_primary: true,
      });

      // Update local state
      const updatedImages = images.map((img) => ({
        ...img,
        is_primary: img.id === imageId,
      }));
      onImagesChange(updatedImages);

      // Show success message
      toast.success('Primary image updated successfully');
    } catch (error: any) {
      console.error('Error setting primary image:', error);
      console.error('Error details:', error.response?.data, error.response?.status);
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to set primary image';
      toast.error(errorMessage);
    } finally {
      // Remove loading state
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`primary-${imageId}`);
        return newSet;
      });
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    const image = images.find((img) => img.id === imageId);
    if (!image) return;

    const isConfirmed = window.confirm(
      `Are you sure you want to delete "${image.file_name}"?\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) return;

    // Add loading state
    setLoadingActions((prev) => new Set(prev).add(`delete-${imageId}`));

    try {
      await apiService.delete(`/vehicles/${vehicleId}/vehicle-images/${imageId}`);

      // Update local state
      const updatedImages = images.filter((img) => img.id !== imageId);
      onImagesChange(updatedImages);

      // Clear selection if image was selected
      setSelectedImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });

      // Show success message
      toast.success('Image deleted successfully');

      // Close modal after successful single delete
      onCloseModal?.();
    } catch (error: any) {
      console.error('Error deleting image:', error);
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to delete image';
      toast.error(errorMessage);
    } finally {
      // Remove loading state
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`delete-${imageId}`);
        return newSet;
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedImages.size === 0) return;

    const isConfirmed = window.confirm(
      `Are you sure you want to delete ${selectedImages.size} image${selectedImages.size > 1 ? 's' : ''}?\n\nThis action cannot be undone.`
    );

    if (!isConfirmed) return;

    // Add loading state for all selected images
    const loadingKeys = Array.from(selectedImages).map((id) => `delete-${id}`);
    setLoadingActions((prev) => {
      const newSet = new Set(prev);
      loadingKeys.forEach((key) => newSet.add(key));
      return newSet;
    });

    try {
      // Delete all selected images
      await Promise.all(
        Array.from(selectedImages).map((imageId) =>
          apiService.delete(`/vehicles/${vehicleId}/vehicle-images/${imageId}`)
        )
      );

      // Update local state
      const updatedImages = images.filter((img) => !selectedImages.has(img.id));
      onImagesChange(updatedImages);

      // Clear selection
      setSelectedImages(new Set());

      // Exit selection mode
      setIsSelectionMode(false);

      // Show success message
      toast.success(
        `${selectedImages.size} image${selectedImages.size > 1 ? 's' : ''} deleted successfully`
      );

      // Close modal after successful bulk delete
      onCloseModal?.();
    } catch (error: any) {
      console.error('Error deleting images:', error);
      const errorMessage =
        error.response?.data?.detail || error.message || 'Failed to delete some images';
      toast.error(errorMessage);
    } finally {
      // Remove loading states
      setLoadingActions((prev) => {
        const newSet = new Set(prev);
        loadingKeys.forEach((key) => newSet.delete(key));
        return newSet;
      });
    }
  };

  const handleImageSelect = (imageId: string) => {
    if (!isSelectionMode) return;

    setSelectedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.size === images.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(images.map((img) => img.id)));
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedImages(new Set());
    }
  };

  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    if (isSelectionMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e: React.DragEvent, index: number) => {
    if (isSelectionMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleImageDragLeave = (_e: React.DragEvent) => {
    if (isSelectionMode) return;
    setDragOverIndex(null);
  };

  const handleImageDrop = async (e: React.DragEvent, dropIndex: number) => {
    if (isSelectionMode) return;
    e.preventDefault();
    setDragOverIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newImages = [...images];
    const [draggedImage] = newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);

    // Update display orders
    const updatedImages = newImages.map((img, index) => ({
      ...img,
      display_order: index,
    }));

    // Update local state immediately
    onImagesChange(updatedImages);

    // Update backend
    try {
      await Promise.all(
        updatedImages.map((img, index) =>
          apiService.patch(`/vehicles/${vehicleId}/vehicle-images/${img.id}`, {
            display_order: index,
          })
        )
      );
    } catch (error) {
      console.error('Error updating display order:', error);
      toast.error('Failed to update image order');
      // Revert on error
      onImagesChange(images);
    }

    setDraggedIndex(null);
  };

  const handleImageDragEnd = () => {
    if (isSelectionMode) return;
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      {/* Source-type banner — explains where these images came from and what can be edited */}
      {hasImages && allExternal && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 rounded-md bg-blue-100 p-2">
              <CloudArrowDownIcon className="h-5 w-5 text-blue-700" />
            </div>
            <div className="flex-1 text-sm">
              <p className="font-semibold text-blue-900">Imported from CarsForSale</p>
              <p className="mt-1 text-xs text-blue-800">
                These photos are managed at the source. To add, remove, reorder, or replace them,
                edit your inventory in CarsForSale — changes will sync on the next import.
              </p>
            </div>
          </div>
        </div>
      )}

      {hasImages && !allExternal && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 rounded-md bg-emerald-100 p-2">
              <ArrowUpTrayIcon className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="flex-1 text-sm">
              <p className="font-semibold text-emerald-900">Local uploads</p>
              <p className="mt-1 text-xs text-emerald-800">
                Uploaded from file, mobile device, or QR — editable here. Drag thumbnails to
                reorder, click ⭐ to set the primary image, or ✕ to delete.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area — hidden when all images come from an external source */}
      {!allExternal && (
        <>
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            } ${images.length >= maxImages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={images.length < maxImages ? openFileDialog : undefined}
          >
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-2">
              <p className="text-sm font-medium text-gray-900">
                {images.length >= maxImages
                  ? `Maximum ${maxImages} images reached`
                  : 'Drop images here or click to browse'}
              </p>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF up to 10MB each</p>
            </div>

            {uploading.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
                <p className="text-sm text-blue-600 mt-2">
                  Uploading {uploading.length} image{uploading.length > 1 ? 's' : ''}...
                </p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={handleFileSelect}
          />
        </>
      )}

      {/* Image Gallery */}
      {images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <h4 className="text-sm font-medium text-gray-900">
                {allExternal
                  ? `Vehicle Images (${images.length})`
                  : `Vehicle Images (${images.length}/${maxImages})`}
              </h4>
              {images.length > 1 && !allExternal && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelectionMode();
                  }}
                  className={`text-xs px-2 py-1 rounded-full transition-colors ${
                    isSelectionMode
                      ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {isSelectionMode ? 'Cancel Selection' : 'Select Multiple'}
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {isSelectionMode && !allExternal && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAll();
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    {selectedImages.size === images.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedImages.size > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBulkDelete();
                      }}
                      className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 flex items-center"
                      disabled={loadingActions.size > 0}
                    >
                      <TrashIcon className="h-3 w-3 mr-1" />
                      Delete ({selectedImages.size})
                    </button>
                  )}
                </>
              )}
              {/* Primary-image hint moved into the banner above (Local uploads) */}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {images
              .sort((a, b) => {
                // Primary image first, then by display order
                if (a.is_primary) return -1;
                if (b.is_primary) return 1;
                return a.display_order - b.display_order;
              })
              .map((image, index) => {
                const isSelected = selectedImages.has(image.id);
                const isPrimaryLoading = loadingActions.has(`primary-${image.id}`);
                const isDeleteLoading = loadingActions.has(`delete-${image.id}`);
                const isExternal = image.isExternalUrl === true;

                return (
                  <div
                    key={image.id}
                    className={`relative group ${isExternal ? 'cursor-default' : isSelectionMode ? 'cursor-pointer' : 'cursor-move'} ${
                      dragOverIndex === index && !isSelectionMode && !isExternal
                        ? 'ring-2 ring-blue-400 ring-opacity-50'
                        : ''
                    } ${draggedIndex === index && !isSelectionMode ? 'opacity-50' : ''} ${
                      isSelected ? 'ring-2 ring-blue-500 ring-opacity-75' : ''
                    }`}
                    draggable={!isSelectionMode && !isExternal}
                    onDragStart={(e) =>
                      !isSelectionMode && !isExternal && handleImageDragStart(e, index)
                    }
                    onDragOver={(e) =>
                      !isSelectionMode && !isExternal && handleImageDragOver(e, index)
                    }
                    onDragLeave={!isSelectionMode && !isExternal ? handleImageDragLeave : undefined}
                    onDrop={(e) => !isSelectionMode && !isExternal && handleImageDrop(e, index)}
                    onDragEnd={!isSelectionMode && !isExternal ? handleImageDragEnd : undefined}
                  >
                    {/* Selection Checkbox */}
                    {isSelectionMode && !isExternal && (
                      <div className="absolute top-2 left-2 z-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleImageSelect(image.id);
                          }}
                          className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
                        />
                      </div>
                    )}

                    <div className="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden bg-gray-200">
                      <img
                        src={apiService.getFileUrl(image.file_path)}
                        alt={image.file_name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src =
                            'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 150"><rect width="200" height="150" fill="%23f3f4f6"/><text x="100" y="75" text-anchor="middle" fill="%236b7280" font-size="12">Image not found</text></svg>';
                        }}
                      />

                      {/* Loading overlay */}
                      {(isPrimaryLoading || isDeleteLoading) && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>

                    {/* Primary Badge */}
                    {image.is_primary && (
                      <div
                        className={`absolute top-2 ${isSelectionMode ? 'left-12' : 'left-2'} z-5`}
                      >
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <StarIconSolid className="h-3 w-3 mr-1" />
                          Primary
                        </span>
                      </div>
                    )}

                    {/* Per-thumbnail source pill removed — banner above explains source for the whole set */}

                    {/* Action Buttons */}
                    {!isSelectionMode && !isExternal && (
                      <div className="absolute top-2 right-2">
                        <div className="flex space-x-1">
                          {/* Primary/Star Toggle Button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!image.is_primary) handleSetPrimary(image.id);
                            }}
                            disabled={image.is_primary || isPrimaryLoading}
                            className={`p-1.5 rounded-full shadow-sm transition-colors ${
                              image.is_primary
                                ? 'bg-blue-100 text-blue-600 cursor-default'
                                : isPrimaryLoading
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-white hover:bg-yellow-50 hover:text-yellow-600'
                            }`}
                            title={
                              image.is_primary ? 'Current primary image' : 'Set as primary image'
                            }
                          >
                            {isPrimaryLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            ) : image.is_primary ? (
                              <StarIconSolid className="h-4 w-4" />
                            ) : (
                              <StarIcon className="h-4 w-4 text-gray-600" />
                            )}
                          </button>

                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(image.id);
                            }}
                            disabled={isDeleteLoading}
                            className={`p-1.5 rounded-full shadow-sm transition-colors ${
                              isDeleteLoading
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white hover:bg-red-50 hover:text-red-600'
                            }`}
                            title="Delete image"
                          >
                            {isDeleteLoading ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            ) : (
                              <XMarkIcon className="h-4 w-4 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* File Name */}
                    <div className="mt-2 text-center">
                      <p className="text-xs text-gray-600 truncate" title={image.file_name}>
                        {image.file_name}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleImageUpload;
